'use client';

import { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Input,
  Progress,
  Row,
  Space,
  Typography,
  Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { CsvMatchyUpload } from '@/app/components/knowledgeBase/csvUpload/csvUploadMatchyLibrary/CsvMatchyUpload';
import type { CsvFileConfig } from '@/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { useManualUpload } from '@/app/hooks/manualUpload/useManualUpload';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

const csvMatchyConfig: CsvFileConfig = {
  name: 'Manual Upload CSV',
  description: 'Map the timestamp and log columns before importing CSV rows.',
  fileName: 'manual_upload.csv',
  columns: [
    {
      key: 'timestamp',
      title: 'Timestamp',
      required: true,
      fieldType: 'string',
    },
    {
      key: 'log',
      title: 'Log',
      required: true,
      fieldType: 'string',
    },
  ],
};

type ManualUploadCenterProps = {
  initialAssetId?: string;
  initialClientName?: string;
  assetName?: string;
  hideAssetField?: boolean;
};

export const ManualUploadCenter = ({
  initialAssetId,
  initialClientName,
  assetName,
  hideAssetField = false,
}: ManualUploadCenterProps) => {
  const {
    t,
    assetId,
    setAssetId,
    files,
    activePreview,
    setActivePreview,
    globalError,
    setGlobalError,
    summary,
    isUploading,
    isSubmittingCsv,
    supportedFormatsLabel,
    addFiles,
    removeFile,
    startUpload,
    submitCsvRows,
  } = useManualUpload({
    initialAssetId,
    initialClientName,
  });

  const currentPreview = activePreview ?? files[0] ?? null;

  const alertType = useMemo(() => {
    if (!summary) {
      return 'success';
    }

    return summary.failedCount > 0 ? 'warning' : 'success';
  }, [summary]);

  return (
    <Flex vertical gap={24}>
      <Card>
        <Flex vertical gap={20}>
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              {t('title')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {t('subtitle')}
            </Paragraph>
          </div>

          {hideAssetField && assetId ? (
            <Alert
              type="info"
              showIcon
              message={assetName ?? t('assetIdLabel')}
              description={`${t('assetIdLabel')}: ${assetId}`}
            />
          ) : null}

          <Row gutter={16}>
            {!hideAssetField && (
              <Col xs={24}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>{t('assetIdLabel')}</Text>
                  <Input
                    value={assetId}
                    onChange={(event) => setAssetId(event.target.value)}
                    placeholder={t('assetIdPlaceholder')}
                    disabled={isUploading || isSubmittingCsv}
                  />
                </Space>
              </Col>
            )}
          </Row>

          {globalError && (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setGlobalError(null)}
              message={t('error')}
              description={globalError}
            />
          )}

          <Dragger
            multiple
            beforeUpload={(file) => {
              addFiles([file]);
              return false;
            }}
            showUploadList={false}
            disabled={isUploading}
            accept=".csv,.json,.txt,.zip,.tar,.tar.gz,.tgz"
          >
            <Flex vertical align="center" gap={8}>
              <InboxOutlined style={{ fontSize: 44, color: '#1E6DF6' }} />
              <Text strong>{t('dropzoneTitle')}</Text>
              <Text type="secondary">{t('dropzoneHint')}</Text>
              <Text type="secondary">{supportedFormatsLabel}</Text>
            </Flex>
          </Dragger>

          <Flex justify="space-between" align="center">
            <Text>{t('queuedFiles', { count: files.length })}</Text>
            <Button
              type="primary"
              onClick={() => void startUpload()}
              loading={isUploading}
              disabled={files.length === 0}
            >
              {t('startUpload')}
            </Button>
          </Flex>

          <Row gutter={16}>
            <Col xs={24} lg={14}>
              <Flex vertical gap={12}>
                {files.map((file) => (
                  <Card
                    key={file.id}
                    size="small"
                    onClick={() => setActivePreview(file)}
                    style={{
                      cursor: 'pointer',
                      borderColor:
                        currentPreview?.id === file.id ? '#1E6DF6' : undefined,
                    }}
                    extra={
                      <Button
                        type="link"
                        danger
                        onClick={(event) => {
                          event.stopPropagation();
                          removeFile(file.id);
                        }}
                        disabled={file.status === 'uploading'}
                      >
                        {t('remove')}
                      </Button>
                    }
                  >
                    <Flex vertical gap={8}>
                      <Text strong>{file.file.name}</Text>
                      <Text type="secondary">
                        {(file.file.size / (1024 * 1024)).toFixed(2)} MB
                      </Text>
                      <Progress
                        percent={file.progress}
                        status={
                          file.status === 'error' ? 'exception' : undefined
                        }
                      />
                      {file.error && (
                        <Alert type="error" showIcon message={file.error} />
                      )}
                    </Flex>
                  </Card>
                ))}
              </Flex>
            </Col>

            <Col xs={24} lg={10}>
              <Card title={t('preview')}>
                {!currentPreview ? (
                  <Alert
                    type="info"
                    showIcon
                    message={t('noFileSelected')}
                    description={t('previewHint')}
                  />
                ) : (
                  <Flex vertical gap={12}>
                    <Text strong>{currentPreview.file.name}</Text>
                    <Text>
                      {t('sizeLabel', {
                        size: (
                          currentPreview.file.size /
                          (1024 * 1024)
                        ).toFixed(2),
                      })}
                    </Text>
                    <Text>
                      {t('statusLabel', { status: currentPreview.status })}
                    </Text>
                    <Progress
                      type="circle"
                      percent={currentPreview.progress}
                      status={
                        currentPreview.status === 'error'
                          ? 'exception'
                          : undefined
                      }
                    />
                  </Flex>
                )}
              </Card>
            </Col>
          </Row>
        </Flex>
      </Card>

      <Card title={t('csvMatchyTitle')}>
        <Flex vertical gap={16}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('csvMatchyDescription')}
          </Paragraph>
          {isSubmittingCsv && <Progress percent={70} status="active" />}
          <CsvMatchyUpload
            config={csvMatchyConfig}
            onSubmitDataAction={(rows) => {
              void submitCsvRows(rows);
            }}
          />
        </Flex>
      </Card>

      {summary && (
        <Alert
          type={alertType}
          showIcon
          message={
            summary.failedCount > 0
              ? t('summaryWithWarnings')
              : t('summarySuccess')
          }
          description={
            <Flex vertical gap={8}>
              <Text>
                {t('insertedCount', { count: summary.insertedCount })}
              </Text>
              <Text>{t('failedCount', { count: summary.failedCount })}</Text>
              <Text>
                {t('processedFileCount', {
                  count: summary.processedFileCount,
                })}
              </Text>
              <Text>
                {t('skippedEntryCount', {
                  count: summary.skippedEntryCount,
                })}
              </Text>
              {summary.errors.length > 0 && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  {summary.errors.slice(0, 10).map((error) => (
                    <Text
                      key={`${error.sourcePath}-${error.message}`}
                      type="danger"
                    >
                      {error.sourcePath}: {error.message}
                    </Text>
                  ))}
                </>
              )}
            </Flex>
          }
        />
      )}
    </Flex>
  );
};
